import { useState } from 'react';
import clsx from 'clsx';
import styles from './ReconPill.module.scss';
import ReconciliationsDrawer from '../ReconciliationsDrawer';
import DegradedIndicator from './DegradedIndicator';
import PillArrowGlyph from '../../../../../common/PillArrowGlyph/PillArrowGlyph';
import { useEnsureReconciliations } from '../hooks/useEnsureReconciliations';
import { useAppSelector } from '../../../../../app/hooks';
import { selectReconciliationsEntry, selectScopedDegraded, selectSectionsForScope } from '../../../../../store/reconciliations/selectors';
import type { ReconScope } from '../../../../../store/reconciliations/constants';
import { useVisibilityConfig } from '../../../../../utils/useVisibilityConfig';
import { VISIBILITY_KEYS } from '../../../../../utils/constants/visibilityKeys';

interface ReconPillProps {
    orgId: string;
    scope: ReconScope;
}

type PillStatus = 'RECONCILED' | 'PARTIAL' | 'NOT_RECONCILED';

const STATUS_LABEL: Record<PillStatus, string> = {
    RECONCILED: 'Reconciled',
    PARTIAL: 'Partially Reconciled',
    NOT_RECONCILED: 'Not Reconciled',
};

// Worst-first, matching the server's own "row status = worst non-NA cell" rule (D13) — the
// pill takes the worst of the already-computed per-recon statuses rather than re-deriving score
// bands client-side, so it never drifts from the server's scoring formula. This status-only reduce
// picks the same recon as the underwriting-engine comparator's own winner — its band is monotone in
// |score - 100| — so the table below is not a second copy of it and must not be "fixed" into one.
const STATUS_SEVERITY: Record<PillStatus, number> = { NOT_RECONCILED: 2, PARTIAL: 1, RECONCILED: 0 };

/** One component mounted 4x (Banking, Financials, GST, Debt Details) with a `scope` prop —
 *  reads through the shared cache (useEnsureReconciliations), so mounting beside the chip or
 *  switching between pills never adds a request (A-62). Gates on the same opt-out key as the
 *  sidebar chip (D4) here, in the one shared component, rather than at each of the 4 host call
 *  sites — keeps the visibility check in one place instead of 4 copy-pasted lookups. */
function ReconPill({ orgId, scope }: Readonly<ReconPillProps>) {
    const [showDrawer, setShowDrawer] = useState(false);
    const { isConfigLoaded, getFeatureVisibility } = useVisibilityConfig();
    const isReconciliationsVisible = isConfigLoaded && getFeatureVisibility(VISIBILITY_KEYS.RECONCILIATIONS.DRAWER_ENABLE).show;

    useEnsureReconciliations(isReconciliationsVisible ? orgId : undefined, null);
    const entry = useAppSelector((state) => selectReconciliationsEntry(state, orgId, null));
    const sections = selectSectionsForScope(entry, scope);

    // The scoped status for this pill (D6) — worst of the already-computed per-recon statuses,
    // limited to the recons this scope shows.
    const statuses = sections
        .flatMap((section) => section.recons.map((recon) => recon.status))
        .filter((s): s is PillStatus => s === 'RECONCILED' || s === 'PARTIAL' || s === 'NOT_RECONCILED');
    const status = statuses.length ? statuses.reduce((worst, s) => (STATUS_SEVERITY[s] > STATUS_SEVERITY[worst] ? s : worst)) : null;

    if (!isReconciliationsVisible || entry?.status !== 'success' || status === null) return null;

    // Scoped like the status above it (D6): the payload-wide `degraded` names recons from every
    // section, and this pill can only show its own scope's.
    const scopedDegraded = selectScopedDegraded(entry.payload?.degraded, sections);

    return (
        <>
            <button type="button" className={clsx(styles.ReconPill, styles[status])} onClick={() => setShowDrawer(true)}>
                <PillArrowGlyph className={styles.PillArrowGlyph} />
                {STATUS_LABEL[status]}
                {scopedDegraded && <DegradedIndicator degraded={scopedDegraded} />}
            </button>
            {showDrawer && <ReconciliationsDrawer orgId={orgId} scope={scope} onClose={() => setShowDrawer(false)} />}
        </>
    );
}

export default ReconPill;
